import React, { FC } from 'react';
import { ColorFunction } from '@components/styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { IMAGE_ZOOM_CONSTRAINTS } from '@v2/components/imageHandling/constants';

interface ZoomControlsProps {
  C: ColorFunction;
  scale: ScaleFunction;
  zoom: number;
  onZoomIn: (e: React.MouseEvent) => void;
  onZoomOut: (e: React.MouseEvent) => void;
  /** Optional positioning override; defaults to bottom: 35, right: 20 (scaled). */
  bottom?: number;
  right?: number;
  /** Optional zIndex override (used when controls float above the drawing overlay). */
  zIndex?: number;
}

const ZoomControls: FC<ZoomControlsProps> = ({ C, scale, zoom, onZoomIn, onZoomOut, bottom = 35, right = 20, zIndex }) => {
  const ZOOM_CONTROL_BG = C('Overlay/Light/Picture dark');
  const ZOOM_CONTROL_ICON_COLOR = C('Label / Light / AlwaysLight Primary');
  const ZOOM_CONTROL_BORDER_COLOR = C('Label/Dark/Always Light Tertiary');

  return (
    <div
      style={{
        position: 'absolute',
        bottom: scale(bottom),
        right: scale(right),
        ...(zIndex != null ? { zIndex } : {}),
      }}
    >
      <div
        onClick={onZoomIn}
        onDoubleClick={(e) => e.stopPropagation()}
        style={{
          background: ZOOM_CONTROL_BG,
          color: zoom >= IMAGE_ZOOM_CONSTRAINTS.max ? ZOOM_CONTROL_BORDER_COLOR : ZOOM_CONTROL_ICON_COLOR,
          width: scale(36),
          height: scale(36),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          border: `1px solid ${ZOOM_CONTROL_BORDER_COLOR}`,
          borderTopLeftRadius: scale(4),
          borderTopRightRadius: scale(4),
          pointerEvents: zoom >= IMAGE_ZOOM_CONSTRAINTS.max ? 'none' : 'auto',
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path
            d="M0 9C0 9.66667 0.548387 10.2043 1.2043 10.2043H7.7957V16.7957C7.7957 17.4516 8.34409 18 9 18C9.65591 18 10.2043 17.4516 10.2043 16.7957V10.2043H16.7957C17.4516 10.2043 18 9.66667 18 9C18 8.34409 17.4516 7.7957 16.7957 7.7957H10.2043V1.2043C10.2043 0.548387 9.65591 0 9 0C8.34409 0 7.7957 0.548387 7.7957 1.2043V7.7957H1.2043C0.548387 7.7957 0 8.34409 0 9Z"
            fill="currentColor"
          />
        </svg>
      </div>
      <div
        onClick={onZoomOut}
        onDoubleClick={(e) => e.stopPropagation()}
        style={{
          background: ZOOM_CONTROL_BG,
          color: zoom <= IMAGE_ZOOM_CONSTRAINTS.min ? ZOOM_CONTROL_BORDER_COLOR : ZOOM_CONTROL_ICON_COLOR,
          width: scale(36),
          height: scale(36),
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          border: `1px solid ${ZOOM_CONTROL_BORDER_COLOR}`,
          borderBottomLeftRadius: scale(4),
          borderBottomRightRadius: scale(4),
          marginTop: -scale(1),
          pointerEvents: zoom <= IMAGE_ZOOM_CONSTRAINTS.min ? 'none' : 'auto',
        }}
      >
        <svg width="18" height="4" viewBox="0 0 18 4" fill="none">
          <rect y="0.799805" width="18" height="2.4" rx="1.2" fill="currentColor" />
        </svg>
      </div>
    </div>
  );
};

export default ZoomControls;
